import * as Bixby from '$/core';

export abstract class CloDealService {
  private static readonly baseUrl = Bixby.Constants.API_CLOCONTROLLER;
  private static readonly prefixConstant = "deal";
  private static readonly assetMappingConstant = "holdingMappings";
  private static readonly cloDealBixbyIdMappingConstant = "dealBixbyMappings";
  private static readonly addAssetMappingConstant = "addHoldingMappings";
  private static readonly removeAssetMappingConstant = "removeHoldingMapping";
  private static readonly updateAssetMappingConstant = "updateHoldingMapping";
  private static readonly companiesByInstrumentIdConstant = "companiesByInstrumentId";
  private static readonly dealManagerConstant = "dealmanager";

  // Client
  public static async getCloAnalytics(): Promise<Bixby.Models.CloAnalytics[]> {
    const route = Bixby.API.ServerRoute.forAction(`${CloDealService.baseUrl}/${CloDealService.prefixConstant}${Bixby.Constants.API_ACTION_CLO_ANALYTICS}`);
    return await Bixby.API.get(route);
  }

  public static async getCloAnalyticsById(cloId: string): Promise<Bixby.Models.CloAnalytics[]> {
    const route = Bixby.API.ServerRoute.forAction(
      `${CloDealService.baseUrl}/${Bixby.Constants.API_ACTION_CLO_DEAL_INDUSTRIES}/${cloId}`
    );
    return await Bixby.API.get(route);
  }

  public static async getCloById(cloId: string): Promise<Bixby.Models.CloDeal> {
    const route = Bixby.API.ServerRoute.forAction(`${CloDealService.baseUrl}/${CloDealService.prefixConstant}/${cloId}`);
    const results: Bixby.Models.CloDeal = await Bixby.API.get(route);
    return results;
  }

  public static async getSummaryAnalyticsById(cloId: string): Promise<Bixby.Models.CloAnalytics[]> {
    const route = Bixby.API.ServerRoute.forAction(
      `${CloDealService.baseUrl}/${CloDealService.prefixConstant}${Bixby.Constants.API_ACTION_CLO_ANALYTICS}/${cloId}`
    );
    return await Bixby.API.get(route);
  }

  public static async getSummaryAnalyticsByDate(closingDate: Date | undefined): Promise<Bixby.Models.CloAnalytics[]> {
    const route = Bixby.API.ServerRoute.forAction(
      `${CloDealService.baseUrl}/${CloDealService.prefixConstant}${Bixby.Constants.API_ACTION_CLO_ANALYTICS}/${closingDate}`
    );
    const results: Bixby.Models.CloAnalytics[] = await Bixby.API.get(route);
    return results;
  }
  
  public static async getAllClos(): Promise<Bixby.Models.CloDealSlim[]> {
    const route = Bixby.API.ServerRoute.forAction(`${Bixby.Constants.API_ADMIN_PREFIX}/${CloDealService.baseUrl}/${CloDealService.prefixConstant}`);
    const results: Bixby.Models.CloDealSlim[] = await Bixby.API.get(route);
    return results;
  }

  public static async getAllCloDealManagers(): Promise<Bixby.Models.CloDealManager[]> {
    const route = Bixby.API.ServerRoute.forAction(`${Bixby.Constants.API_ADMIN_PREFIX}/${CloDealService.baseUrl}/${CloDealService.dealManagerConstant}`);
    const results: Bixby.Models.CloDealManager[] = await Bixby.API.get(route);
    return results; 
  }

  public static async createClo(newClo: Bixby.Models.CreateCloDealRequest): Promise<Bixby.Models.CloDeal> {
    const route = Bixby.API.ServerRoute.forAction(`${Bixby.Constants.API_ADMIN_PREFIX}/${CloDealService.baseUrl}/${CloDealService.prefixConstant}`);
    const result: Bixby.Models.CloDeal = await Bixby.API.post(route, newClo);
    return result;
  }

  public static async updateClo(updateRequest: Bixby.Models.CloDealSlim): Promise<Bixby.Models.CloDeal> {
    const route = Bixby.API.ServerRoute.forAction(
      `${Bixby.Constants.API_ADMIN_PREFIX}/${CloDealService.baseUrl}/${Bixby.Constants.API_ACTION_UPDATE}${CloDealService.prefixConstant}`
    );
    const result: Bixby.Models.CloDeal = await Bixby.API.post(route, updateRequest);
    return result;
  }

  public static async addClientClo(clientId: string, cloId: string) {
    const client = `${Bixby.Constants.API_CLIENTCONTROLLER}/${clientId}`;
    const clo = `${Bixby.Constants.API_ACTION_CLO}${CloDealService.prefixConstant}/${cloId}`;
    const route = Bixby.API.ServerRoute.forAction(`${Bixby.Constants.API_ADMIN_PREFIX}/${client}/${clo}`);
    return await Bixby.API.post(route, null);
  }

  public static async deleteClientClo(clientId: string, cloId: string) {
    const client = `${Bixby.Constants.API_CLIENTCONTROLLER}/${clientId}`;
    const clo = `${Bixby.Constants.API_ACTION_CLO}${CloDealService.prefixConstant}/${cloId}`;
    const route = Bixby.API.ServerRoute.forAction(`${Bixby.Constants.API_ADMIN_PREFIX}/${client}/${clo}`);
    return await Bixby.API.delete(route);
  }

  
  public static async uploadZipfile(cloId: string, file: File) {
    const url = `${Bixby.Constants.API_ADMIN_PREFIX}/${CloDealService.baseUrl}/${cloId}/${Bixby.Constants.API_ACTION_SUBMIT}${CloDealService.prefixConstant}`;
    const route = Bixby.API.ServerRoute.forAction(url);

    const formData = new FormData();
    formData.append('file', file);

    return await Bixby.API.postJSON(route, formData);
  }

  public static async downloadSFDRReportById(cloId: string) {
    const route = Bixby.API.ServerRoute.forAction(
      `${CloDealService.baseUrl}/${Bixby.Constants.API_ACTION_DOWNLOAD}/${Bixby.Constants.API_ACTION_CLO_SFDR_REPORT}/${cloId}`
    );

    const blob = await Bixby.API.getFileWithMetadata(route);
    
    return blob;
  }

  public static async addClientCloDealManager(clientId: string, managerId: string) {
    const client = `${Bixby.Constants.API_CLIENTCONTROLLER}/${clientId}`;
    const clo = `${Bixby.Constants.API_ACTION_CLO}${CloDealService.dealManagerConstant}/${managerId}`;
    const route = Bixby.API.ServerRoute.forAction(`${Bixby.Constants.API_ADMIN_PREFIX}/${client}/${clo}`);
    return await Bixby.API.post(route, null);
  }

  public static async deleteClientCloDealManager(clientId: string, managerId: string) {
    const client = `${Bixby.Constants.API_CLIENTCONTROLLER}/${clientId}`;
    const clo = `${Bixby.Constants.API_ACTION_CLO}${CloDealService.dealManagerConstant}/${managerId}`;
    const route = Bixby.API.ServerRoute.forAction(`${Bixby.Constants.API_ADMIN_PREFIX}/${client}/${clo}`);
    return await Bixby.API.delete(route);
  }
  
  public static async getAllCloDealHoldingForBixbyIdMappings(): Promise<Bixby.Models.CloDealHoldingForBixbyIdMapping[]> {
    const route = Bixby.API.ServerRoute.forAction(`${Bixby.Constants.API_ADMIN_PREFIX}/${CloDealService.baseUrl}/${CloDealService.prefixConstant}/${CloDealService.assetMappingConstant}`);
    const results: Bixby.Models.CloDealHoldingForBixbyIdMapping[] = await Bixby.API.get(route);
    return results;
  }
  
  public static async getAllCloDealBixbyIdMappings(): Promise<Bixby.Models.CloDealBixbyIdMapping[]> {
    const route = Bixby.API.ServerRoute.forAction(`${Bixby.Constants.API_ADMIN_PREFIX}/${CloDealService.baseUrl}/${CloDealService.prefixConstant}/${CloDealService.cloDealBixbyIdMappingConstant}`);
    const results: Bixby.Models.CloDealBixbyIdMapping[] = await Bixby.API.get(route);
    return results;
  }
  
  public static async addCloBixbyIdMappings(payLoad: Bixby.Models.CloDealBixbyIdMapInput[]): Promise<boolean> {
    const route = Bixby.API.ServerRoute.forAction(`${Bixby.Constants.API_ADMIN_PREFIX}/${CloDealService.baseUrl}/${CloDealService.prefixConstant}/${CloDealService.addAssetMappingConstant}`);
    const result: boolean = await Bixby.API.post(route, payLoad);
    return result;
  }
  
  public static async removeCloBixbyIdMapping(id: string): Promise<boolean> {
    const route = Bixby.API.ServerRoute.forAction(`${Bixby.Constants.API_ADMIN_PREFIX}/${CloDealService.baseUrl}/${CloDealService.prefixConstant}/${id}/${CloDealService.removeAssetMappingConstant}`);
    const result: boolean = await Bixby.API.delete(route);
    return result;
  }
  
  public static async updateCloDealBixIdMapping(id: string, bixbyId: string): Promise<boolean> {
    const route = Bixby.API.ServerRoute.forAction(`${Bixby.Constants.API_ADMIN_PREFIX}/${CloDealService.baseUrl}/${CloDealService.prefixConstant}/${id}/${CloDealService.updateAssetMappingConstant}/${bixbyId}`);
    const result: boolean = await Bixby.API.post(route, null);
    return result;
  }

  public static async getAllCloDealCompanies(instrumentId: string): Promise<Bixby.Models.AdminCompanyForClientAdd[]> {
    const route = Bixby.API.ServerRoute.forAction(`${Bixby.Constants.API_ADMIN_PREFIX}/${CloDealService.baseUrl}/${CloDealService.prefixConstant}/${instrumentId}/${CloDealService.companiesByInstrumentIdConstant}`);
    const result: Bixby.Models.AdminCompanyForClientAdd[] = await Bixby.API.get(route);
    return result;
  }
  
}